/**
 * SUB-3.10 · FR54, FR69, FR56. Moving a client account to a different provider.
 *
 * Pure, and here rather than in the drawer, because the three things it decides are
 * the three things a superadmin has to be able to trust before clicking:
 *
 *   which accounts are even capable of receiving this client account
 *   what the confirmation SAYS will happen, in the words of what will happen
 *   what the toast reports afterwards, from the counts the database measured
 *
 * ⚠️ NOTHING HERE DECIDES WHETHER A TRANSFER IS ALLOWED. FR69's four refusals
 * (destination archived, identical to the current provider, without sub-accounts
 * enabled, or at its allowance) all live in `transfer_sub_account`, and the
 * one-level rules live in `trg_enforce_one_level_hierarchy`. Each names what has to
 * happen first and arrives here as the database's own sentence. What this module
 * does is keep the OBVIOUSLY impossible destinations out of the picker, so those
 * refusals are rare rather than routine. A refusal that is unreachable from the UI
 * is still the rule; a rule restated in TypeScript is a second definition that
 * drifts, and the one the user reads would be the wrong one.
 *
 * ⚠️ AND NOTHING HERE COUNTS SUB-ACCOUNTS. Whether a destination has room is
 * `sub_account_usage()`, which applies FR67: an archived sub-account does not
 * consume the allowance. A count in the browser would disagree with the server's
 * the first time a sub-account was archived, so the drawer asks the usage endpoint
 * about the chosen destination instead. `sub_accounts_enabled` is a capability flag
 * and is the only hierarchy field this module reads.
 */

/** The fields of a tenant row this module needs. Structural, not the whole DTO. */
export interface TransferTenantRow {
  tenant_id: string;
  company_name: string;
  parent_tenant_id?: string | null;
  is_archived?: boolean | null;
  sub_accounts_enabled?: boolean | null;
}

/** What `POST /tenants/{id}/transfer` answers with. */
export interface TransferOutcome {
  status: string;
  former_parent_tenant_id?: string | null;
  new_parent_tenant_id?: string | null;
  access_revoked?: boolean;
  grants_revoked?: number;
  access_granted?: boolean;
  grants_granted?: number;
  child_is_archived?: boolean;
}

/**
 * The accounts a given client account could be moved to.
 *
 * Four exclusions, each mirroring a refusal the database would raise, and each one
 * a fact this list already holds rather than a count it would have to derive:
 *
 *   the client account itself           `tenants_parent_not_self`
 *   its current provider                FR69's identity refusal
 *   accounts without sub-accounts on    FR69, and rule 2 means a sub-account can
 *                                       never have the flag set, so this exclusion
 *                                       also removes every sub-account and with it
 *                                       rule 3's "one level deep"
 *   archived accounts                   B7 of `agency_grant_basis_holds`: an
 *                                       archived agency reaches nowhere, so the
 *                                       transfer would grant nothing
 *
 * The allowance is NOT one of them. See the note at the top of the file.
 *
 * Sorted by name, case-insensitively, so the list is stable between renders. The
 * database's own collation is not consulted because nothing here round-trips a sort
 * order; this is display only.
 */
export function eligibleTransferDestinations<T extends TransferTenantRow>(
  tenants: readonly T[],
  child: TransferTenantRow | null | undefined,
): T[] {
  if (!child?.tenant_id) return [];
  return tenants
    .filter(
      (t) =>
        t.tenant_id !== child.tenant_id &&
        t.tenant_id !== child.parent_tenant_id &&
        t.sub_accounts_enabled === true &&
        !t.is_archived,
    )
    .slice()
    .sort((a, b) =>
      a.company_name.localeCompare(b.company_name, undefined, {
        sensitivity: "base",
      }),
    );
}

/**
 * Why the picker is empty, when it is empty.
 *
 * "No destinations" and "nothing loaded" are different facts, and an empty list
 * rendered without a reason reads as the first when it may be the second. Returns
 * null when there is something to choose, so the caller can render the picker.
 */
export function describeNoDestinations(
  candidateCount: number,
  loadFailed: boolean,
): string | null {
  if (loadFailed) {
    return "The list of accounts could not be loaded, so there is nothing to choose from. Try again.";
  }
  if (candidateCount === 0) {
    return (
      "No other account can receive this client account yet. A destination has to " +
      "have sub-accounts enabled and must not be archived."
    );
  }
  return null;
}

/**
 * FR54 and FR56 in the confirmation, which is the one place a superadmin can still
 * stop.
 *
 * It names both providers, says that the access change happens in one step, and
 * states plainly that no data moves. All three are load-bearing: this is the single
 * most sensitive action in the hierarchy, because it changes WHICH AGENCY CAN READ A
 * CUSTOMER'S DATA, and "are you sure?" would leave the operator to guess at every
 * part of it.
 *
 * The numbers of grants are deliberately NOT predicted. Reading them would take a
 * query whose answer could be stale by the time the button is pressed, and the real
 * figures come back from the transfer itself: see `describeTransferOutcome`.
 */
export function transferConfirmation(
  childName: string,
  fromName: string | null,
  toName: string,
): { title: string; description: string; confirmLabel: string } {
  const from = fromName?.trim() ? fromName : "its current provider";
  return {
    title: `Move ${childName} to ${toName}?`,
    description:
      `${childName} keeps everything it has. Its projects, documents, users, ` +
      `pipelines, checklists and settings are untouched and it keeps the same ` +
      `account id. What changes is who manages it: every Admin entry that exists ` +
      `only because someone is an Agency Admin of ${from} is removed, and the ` +
      `equivalent entries for ${toName}'s Agency Admins are created, in the same ` +
      `step. There is no moment in which both or neither can reach the account. ` +
      `Anyone from ${from} currently working inside ${childName} loses it ` +
      `immediately. Neither provider has to agree, and neither is notified by ` +
      `this action.`,
    confirmLabel: `Move to ${toName}`,
  };
}

/** "entry" / "entries", agreeing with the number in front of it. */
function entries(count: number): string {
  return count === 1 ? "entry" : "entries";
}

/**
 * The sentence after the fact, built from what the database counted.
 *
 * ⚠️ BOTH NUMBERS ARE MEASUREMENTS, NOT ESTIMATES. `transfer_sub_account` reads
 * `user_roles` either side of its own UPDATE, with the reconciling trigger firing in
 * between and no commit anywhere. SUB-3.9 inherited a payload whose `access_revoked`
 * had been a hardcoded `false` while the revocation worked, for as long as the
 * trigger had existed; a UI that invented "1 entry moved" would be the same lie
 * relocated.
 *
 * Zero on the granting side is reported honestly rather than hidden, and it has two
 * innocent causes worth distinguishing in the wording: the destination has no Agency
 * Admins appointed yet, or the client account is archived and holds no live
 * parent-derived grants at all until it is restored.
 */
export function describeTransferOutcome(
  childName: string,
  toName: string,
  outcome: TransferOutcome | null | undefined,
): string {
  const revoked = outcome?.grants_revoked ?? 0;
  const granted = outcome?.grants_granted ?? 0;

  const head = `${childName} is now managed by ${toName}.`;

  const revokedPart =
    revoked > 0
      ? `${revoked} previous admin ${entries(revoked)} removed`
      : "there were no previous admin entries to remove";

  if (granted > 0) {
    return `${head} ${granted} new admin ${entries(granted)} created, ${revokedPart}.`;
  }

  if (outcome?.child_is_archived) {
    return (
      `${head} No admin entries were created because ${childName} is archived; ` +
      `${toName}'s Agency Admins gain access when it is restored. ${revokedPart}.`
    );
  }

  return (
    `${head} No admin entries were created, because ${toName} has no Agency ` +
    `Admins appointed yet. ${revokedPart}.`
  );
}
